<template>
  <Breadcrumbs
    :title="isEdit ? 'Edit Prompt' : 'Create Prompt'"
    :buttonLabel="'Back'"
    @buttonClick="createPrompt"
  />

  <div class="container-fluid">
    <div class="row">
      <!-- LEFT: Main form -->
      <div class="col-12 col-lg-8">
        <BaseForm :fields="formFields" v-model="formData" @submit="handleSubmit" />

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button
            class="btn btn-outline-secondary"
            @click="handleCheckResponse"
            :disabled="!canCheckResponse || isCheckingResponse"
          >
            <span v-if="isCheckingResponse" class="spinner-border spinner-border-sm me-2" />
            Check Response
          </button>

          <button class="btn btn-primary" @click="handleSubmit" :disabled="isFormInvalid">
            Save Prompt
          </button>
        </div>

        <!-- Prompt preview (variables highlighted) -->
<div class="alert alert-info mt-3" :key="previewTick">
  <strong>Preview:</strong>&nbsp;&nbsp;&nbsp;
  <p v-html="resolvedPrompt"></p>
</div>

        <!-- OpenAI response (HTML) -->
        <div class="card mt-3" v-if="responseHtml || isCheckingResponse">
          <div class="card-header d-flex align-items-center justify-content-between">
            <span>Response</span>
            <small v-if="lastCheckedAt" class="text-muted">Last checked: {{ lastCheckedAt }}</small>
          </div>
          <div class="card-body">
            <div v-if="isCheckingResponse" class="text-center text-muted py-3">
              <div class="spinner-border" role="status" />
              <div class="mt-2">Generating response…</div>
            </div>
            <div v-else v-html="responseHtml"></div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Category + Variables -->
<!-- RIGHT: Category + Variables -->
<div class="col-12 col-lg-4 mt-4 mt-lg-0">
  <div class="card">
    <div class="card-body">
      <h5 class="card-title mb-3">Category</h5>

      <!-- Category select (unchanged) -->
      <select class="form-select" v-model="formData.prompt_category_id" aria-label="Select Category">
        <option disabled value="">-- Select Category --</option>
        <option v-for="c in categories" :key="c._id" :value="c._id">
          {{ c.title }}
        </option>
      </select>

      <!-- Variables list (unchanged) -->
      <div class="mt-4">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <h6 class="mb-0">Variables</h6>
          <small class="text-muted" v-if="!formData.prompt_category_id">
            Select a category
          </small>
        </div>

        <div v-if="promptVariables.length">
          <span
            v-for="v in promptVariables"
            :key="v._id"
            class="badge bg-secondary mx-1 mb-1 cursor-pointer"
            @click="insertVariable(v.title)"
          >
            {{ v.title }}
          </span>
        </div>
        <div v-else class="text-muted">No variables to show.</div>
      </div>

      <!-- NEW: Test Idea textbox -->
      <div class="mt-4">
        <label for="test-idea" class="form-label">Test Idea</label>
        <input
          id="test-idea"
          type="text"
          class="form-control"
          placeholder="Type an idea to replace {idea}"
          v-model="formData.idea"
        />
        <small class="text-muted">
          This replaces <code>{idea}</code> in the preview (and in the test request).
        </small>
      </div>
    </div>
  </div>

  <!-- Tips card (optional, unchanged) -->
  <div class="card mt-3">
    <div class="card-body small text-muted">
      Click a variable to insert it into the Prompt at the cursor position.<br />
      The “Test Idea” box live-replaces <code>{idea}</code> in the preview.
    </div>
  </div>
</div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import apiService from '@/services/apiService';
import BaseForm from '@/components/commen/modal/BaseForm.vue';
import { useRoute, useRouter } from 'vue-router';

type Option = { _id: string; title: string };

const route = useRoute();
const router = useRouter();
const createPrompt = () => router.push('/prompts');
const isEdit = computed(() => !!route.query.id);

// ---------- Model ----------
const formData = ref({
  title: '',
  description: '',
  prompt_category_id: '',
  base_prompt_id: '',        // not shown
  workspace_module_id: '',   // not shown
   idea: '',
});
function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
// ---------- Options ----------
const categories = ref<Option[]>([]);
const promptVariables = ref<Array<{ _id: string; title: string; description: string }>>([]);

// ---------- Fields (response removed) ----------
const formFields = computed(() => [
  { key: 'title', label: 'Title', type: 'text', required: true },
  {
    key: 'description',
    label: 'Prompt',
    type: 'textarea',
    required: true,
    placeholder: 'Write prompt with {variables}',
    rows: 10,
  },
]);
const previewTick = ref(0);
const rerenderPreview = () => { previewTick.value++; };
// ---------- Variable highlighting preview ----------
const resolvedPrompt = computed(() => {
  let desc = formData.value.description || '';
  const safeIdea = escapeHtml(formData.value.idea || '');
  desc = desc.replace(/\{idea\}/gi, `<mark>${safeIdea}</mark>`);
for (const v of promptVariables.value) {
    if (/^idea$/i.test(v.title)) continue;            // <-- skip Idea here
    const regex = new RegExp(`\\{${escapeRegExp(v.title)}\\}`, 'g');
    desc = desc.replace(regex, `<mark>${escapeHtml(v.description ?? '')}</mark>`);
  }
  
  return desc;
});

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ---------- Insert variable at caret ----------
const insertVariable = (varName: string) => {
  const el = document.getElementById('baseform-description') as HTMLTextAreaElement | null;
  if (!el) {
    formData.value.description = (formData.value.description || '') + `{${varName}}`;
    return;
  }
  const start = el.selectionStart ?? formData.value.description.length;
  const end = el.selectionEnd ?? formData.value.description.length;
  const before = formData.value.description.slice(0, start);
  const after = formData.value.description.slice(end);
  formData.value.description = before + `{${varName}}` + after;

  requestAnimationFrame(() => {
    const pos = start + varName.length + 2;
    el.focus();
    el.setSelectionRange(pos, pos);
  });
};

// ---------- Loaders ----------
const loadCategories = async () => {
  const res = (await apiService.get('/common/prompt-categories')) || [];
  categories.value = res.data;
};

watch(
  () => formData.value.prompt_category_id,
  async (catId) => {
    if (catId) {
      promptVariables.value =
        (await apiService.get(`/common/prompt-variables-filter?prompt_category_id=${catId}`)) || [];
    } else {
      promptVariables.value = [];
    }
  }
);

// ---------- Check Response (OpenAI preview) ----------
const isCheckingResponse = ref(false);
const responseHtml = ref<string>('');
const lastCheckedAt = ref<string>('');

const canCheckResponse = computed(() =>
  formData.value.title.trim() &&
  formData.value.prompt_category_id.trim() &&
  formData.value.description.trim()
);
function unwrapCodeFence(s) {
  if (!s) return "";

  // Regex strips ```html ... ``` or ``` ... ```
  const m = s.match(/^```(?:\w+)?\s*([\s\S]*?)\s*```$/i);
  let clean = m ? m[1].trim() : s;

  // Unescape any \n, \" etc.
  try {
    clean = JSON.parse(`"${clean.replace(/"/g, '\\"')}"`);
  } catch {
    clean = clean.replace(/\\n/g, '\n'); 
  }

  return clean;
}

const evaluatedPromptForLLM = computed(() => {
  let txt = formData.value.description || '';

  // 1) Replace {idea} case-insensitively with the user's idea
  txt = txt.replace(/\{idea\}/gi, formData.value.idea || '');

  // 2) Replace all other variables with their descriptions
  for (const v of promptVariables.value) {
    if (/^idea$/i.test(v.title)) continue; // don't overwrite the user's idea
    const re = new RegExp(`\\{${escapeRegExp(v.title)}\\}`, 'gi');
    txt = txt.replace(re, (v.description ?? '').toString());
  }

  return txt;
});
const handleCheckResponse = async () => {
  if (!canCheckResponse.value || isCheckingResponse.value) return;

  // re-render immediately on click
  rerenderPreview();

  isCheckingResponse.value = true;
  responseHtml.value = '';

  try {
    const payload = {
      title: formData.value.title,
      raw_description: formData.value.description,
      test_description: evaluatedPromptForLLM.value, // {idea} already swapped
      prompt_category_id: formData.value.prompt_category_id,
      base_prompt_id: formData.value.base_prompt_id || undefined,
      workspace_module_id: formData.value.workspace_module_id || undefined,
    };

    const res = await apiService.post('/common/prompts/preview', payload, { timeout: 600000 });

    const raw =
      res?.html ??
      res?.data?.html ??
      (typeof res === 'string' ? res : '') ?? '';

    const html = unwrapCodeFence(raw);
    responseHtml.value = html?.trim() ? html : '<em>No content returned.</em>';
    lastCheckedAt.value = new Date().toLocaleString();

    // re-render once more after response lands (if inputs changed while waiting)
    rerenderPreview();
  } catch (e: any) {
    responseHtml.value =
      `<div class="text-danger">Failed to generate response: ${e?.response?.data?.message || e?.message || 'Unknown error'}</div>`;
  } finally {
    isCheckingResponse.value = false;
  }
};

// ---------- Submit ----------
const handleSubmit = async () => {
  const id = route.query.id;
  const path = '/common/prompts';
  const payload: any = {
    ...formData.value,
    raw_description: formData.value.description,
    description: resolvedPrompt.value,
  };
  if (!payload.base_prompt_id) delete payload.base_prompt_id;
  if (!payload.workspace_module_id) delete payload.workspace_module_id;

  try {
    const result = isEdit.value
      ? await apiService.put(path, id, payload)
      : await apiService.post(path, payload);
    if (result) router.push('/prompts');
  } catch (error: any) {
    console.error('API Error:', error?.response?.data || error?.message);
  }
};

// ---------- Disable Save (response no longer required) ----------
const isFormInvalid = computed(() => {
  const f = formData.value;
  return !f.title.trim() || !f.prompt_category_id.trim() || !f.description.trim();
});

// ---------- Mount ----------
onMounted(async () => {
  await loadCategories();

  if (isEdit.value) {
    try {
      const id = route.query.id;
      const data = await apiService.get(`/common/prompts/${id}`);

      formData.value = {
        title: data.title || '',
        description: data.raw_description || data.description || '',
        prompt_category_id: data.prompt_category_id || '',
        base_prompt_id: data.base_prompt_id || '',
        workspace_module_id: data.workspace_module_id || '',
      };

      if (formData.value.prompt_category_id) {
        promptVariables.value =
          (await apiService.get(
            `/common/prompt-variables?prompt_category_id=${formData.value.prompt_category_id}`
          )) || [];
      }
    } catch (error: any) {
      console.error('❌ Failed to load prompt data:', error?.response || error);
    }
  }
});
</script>
